import { useState, useCallback, useMemo } from 'react';
import { Activity } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Tooltip as ChartTooltip,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, AMT_CLS, CARD_CLS,
  today, firstOfMonth, fmtDate, fmtAmt, fmtAmtZero, Delta,
  Section, EmptyState, CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  AccountActivityDto, AccountActivityFilter, AccountActivityFlag, AccountActivityRow,
} from '@/types/analytics.types';

// ── Options ───────────────────────────────────────────────────────────────────

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'Asset', label: 'Asset' },
  { value: 'Liability', label: 'Liability' },
  { value: 'Equity', label: 'Equity' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'Expense', label: 'Expense' },
];

/** Top-N cut-off for the two ranking charts — a full chart of 400 accounts is unreadable. */
const TOP_N = 12;

// ── Flags ─────────────────────────────────────────────────────────────────────

const FLAG_LABELS: Record<AccountActivityFlag, string> = {
  INACTIVE:         'Inactive',
  NEGATIVE_BALANCE: 'Negative balance',
  UNUSUAL_ACTIVITY: 'Unusual activity',
  LARGE_ADJUSTMENT: 'Large adjustment',
};

const FLAG_STYLES: Record<AccountActivityFlag, string> = {
  INACTIVE:         'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700',
  NEGATIVE_BALANCE: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
  UNUSUAL_ACTIVITY: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
  LARGE_ADJUSTMENT: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
};

const FlagBadge = ({ flag }: { flag: AccountActivityFlag }) => (
  <span
    className={cn(
      'inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium whitespace-nowrap',
      FLAG_STYLES[flag],
    )}
  >
    {FLAG_LABELS[flag]}
  </span>
);

// ── Summary tile ──────────────────────────────────────────────────────────────

const SummaryTile = ({ label, value, tone }: {
  label: string;
  value: number;
  tone?: 'default' | 'warn' | 'danger';
}) => {
  const valueTone =
    tone === 'danger' && value > 0 ? 'text-red-600 dark:text-red-400'
    : tone === 'warn' && value > 0 ? 'text-amber-600 dark:text-amber-400'
    : 'text-gray-900 dark:text-white';

  return (
    <div className={cn(CARD_CLS, 'p-4 flex flex-col gap-1')}>
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      <span className={cn('text-2xl font-bold tabular-nums', valueTone)}>
        {value.toLocaleString('en-US')}
      </span>
    </div>
  );
};

// ── Chart tooltip ─────────────────────────────────────────────────────────────

const AccountTooltip = ({ active, payload, label, valueLabel, currency }: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
  valueLabel: string;
  currency: boolean;
}) => {
  if (!active || !payload?.length) return null;
  const v = payload[0].value;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200">{label}</p>
      <p className="text-gray-900 dark:text-white font-medium tabular-nums mt-0.5">
        {valueLabel}: {currency ? fmtAmtZero(v) : v.toLocaleString('en-US')}
      </p>
    </div>
  );
};

function fmtCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `${(value / 1_000).toFixed(0)}K`;
  return value.toFixed(0);
}

// ── Excel export ──────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Code',        header: 'Code',            width: 14 },
  { key: 'Name',        header: 'Account Name',    width: 36 },
  { key: 'Type',        header: 'Type',            width: 12 },
  { key: 'Category',    header: 'Category',        width: 16 },
  { key: 'Opening',     header: 'Opening',         width: 16, numFmt: '#,##0.00' },
  { key: 'Debit',       header: 'Debit',           width: 16, numFmt: '#,##0.00' },
  { key: 'Credit',      header: 'Credit',          width: 16, numFmt: '#,##0.00' },
  { key: 'Closing',     header: 'Closing',         width: 16, numFmt: '#,##0.00' },
  { key: 'TxnCount',    header: 'Txn Count',       width: 12 },
  { key: 'AvgValue',    header: 'Avg Value',       width: 16, numFmt: '#,##0.00' },
  { key: 'Largest',     header: 'Largest',         width: 16, numFmt: '#,##0.00' },
  { key: 'Smallest',    header: 'Smallest',        width: 16, numFmt: '#,##0.00' },
  { key: 'LastUsed',    header: 'Last Used',       width: 14 },
  { key: 'DaysSince',   header: 'Days Since',      width: 12 },
  { key: 'Growth',      header: 'Growth %',        width: 12, numFmt: '#,##0.00' },
  { key: 'Flags',       header: 'Flags',           width: 40 },
];

function buildExcelRows(rows: AccountActivityRow[]): Record<string, unknown>[] {
  return rows.map(r => ({
    Code:      r.accountCode,
    Name:      r.accountName,
    Type:      r.accountType,
    Category:  r.accountCategory ?? '',
    Opening:   r.opening,
    Debit:     r.debit,
    Credit:    r.credit,
    Closing:   r.closing,
    TxnCount:  r.transactionCount,
    AvgValue:  r.averageValue,
    Largest:   r.largestTransaction,
    Smallest:  r.smallestTransaction,
    LastUsed:  r.lastUsedDate ?? '',
    DaysSince: r.daysSinceLastUsed ?? '',
    Growth:    r.growthPercent ?? '',
    Flags:     r.flags.map(f => FLAG_LABELS[f]).join(', '),
  }));
}

// ── Component ─────────────────────────────────────────────────────────────────

export const AccountActivityPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<AccountActivityFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
    includeInactive: false,
    exceptionsOnly: false,
  });
  const [data, setData] = useState<AccountActivityDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getAccountActivity(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const rows = useMemo<AccountActivityRow[]>(() => data?.rows ?? [], [data]);

  const topByCount = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.transactionCount - a.transactionCount)
        .slice(0, TOP_N)
        .map(r => ({ label: r.accountCode, count: r.transactionCount })),
    [rows],
  );

  const topByBalance = useMemo(
    () =>
      [...rows]
        .sort((a, b) => Math.abs(b.closing) - Math.abs(a.closing))
        .slice(0, TOP_N)
        .map(r => ({ label: r.accountCode, balance: Math.abs(r.closing) })),
    [rows],
  );

  if (!hasPermission(Permissions.RPT_ACCOUNT_ACTIVITY)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Account Activity',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(data.rows),
      title: 'Account Activity Analysis',
      subtitle: `${data.companyName} · ${data.fromDate} to ${data.toDate}`,
    }, `AccountActivity_${data.fromDate}_${data.toDate}`);
  };

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Account Activity Analysis</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={() => setShowPrint(true)}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3')}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Account Type</label>
            <select
              value={filter.accountTypeFilter ?? ''}
              onChange={e =>
                setFilter(f => ({ ...f, accountTypeFilter: e.target.value || undefined }))
              }
              className={INPUT_CLS}
            >
              {ACCOUNT_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.includeInactive ?? false}
                onChange={e => setFilter(f => ({ ...f, includeInactive: e.target.checked }))}
                className="rounded"
              />
              Include Inactive
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={filter.exceptionsOnly ?? false}
                onChange={e => setFilter(f => ({ ...f, exceptionsOnly: e.target.checked }))}
                className="rounded"
              />
              Exceptions Only
            </label>
          </div>
          <RunReportButton
            onClick={() => { void run(); }}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to analyse account activity.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Report meta */}
          <div className={cn(CARD_CLS, 'px-4 py-3')}>
            <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Account Activity — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
              {' · '}{rows.length} rows
              {' · '}Generated {fmtDate(data.generatedAt)}
            </div>
          </div>

          {/* Summary tiles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <SummaryTile label="Total Accounts"    value={data.totalAccounts} />
            <SummaryTile label="Inactive Accounts" value={data.inactiveAccounts} tone="warn" />
            <SummaryTile label="Flagged Accounts"  value={data.flaggedAccounts} tone="danger" />
          </div>

          {/* Charts */}
          {rows.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Section
                title="Most Active Accounts"
                subtitle={`Top ${Math.min(TOP_N, topByCount.length)} by transaction count`}
              >
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={topByCount} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={54} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <ChartTooltip content={<AccountTooltip valueLabel="Transactions" currency={false} />} />
                    <Bar dataKey="count" name="Transactions" fill={CHART_COLOURS[0]} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Section>

              <Section
                title="Largest Balances"
                subtitle={`Top ${Math.min(TOP_N, topByBalance.length)} by absolute closing balance`}
              >
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={topByBalance} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-gray-100 dark:text-gray-800" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={54} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                    <ChartTooltip content={<AccountTooltip valueLabel="Closing balance" currency />} />
                    <Bar dataKey="balance" name="Closing balance" fill={CHART_COLOURS[1]} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Section>
            </div>
          )}

          {/* Table */}
          <div className={cn(CARD_CLS, 'flex flex-col min-h-0')}>
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Account Detail</h2>
            </div>
            <div className="overflow-auto">
              <table className="w-full text-sm border-collapse min-w-[1600px]">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                  <tr>
                    <th className={cn(TH_CLS, 'w-28')}>Code</th>
                    <th className={TH_CLS}>Account Name</th>
                    <th className={cn(TH_CLS, 'w-24')}>Type</th>
                    <th className={TH_R_CLS}>Opening</th>
                    <th className={TH_R_CLS}>Debit</th>
                    <th className={TH_R_CLS}>Credit</th>
                    <th className={TH_R_CLS}>Closing</th>
                    <th className={TH_R_CLS}>Txn Count</th>
                    <th className={TH_R_CLS}>Avg Value</th>
                    <th className={TH_R_CLS}>Largest</th>
                    <th className={TH_R_CLS}>Smallest</th>
                    <th className={cn(TH_CLS, 'w-28')}>Last Used</th>
                    <th className={TH_R_CLS}>Days Since</th>
                    <th className={TH_R_CLS}>Growth %</th>
                    <th className={TH_CLS}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={15} className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-600">
                        No accounts match the selected criteria.
                      </td>
                    </tr>
                  ) : (
                    rows.map(r => {
                      const flagged = r.flags.length > 0;
                      return (
                        <tr
                          key={r.accountId}
                          className={cn(
                            'border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50',
                            flagged && 'bg-amber-50/50 dark:bg-amber-900/10',
                          )}
                        >
                          <td className="px-3 py-2 text-sm font-mono text-gray-600 dark:text-gray-400">{r.accountCode}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {r.accountName}
                            {r.accountCategory && (
                              <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">({r.accountCategory})</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">{r.accountType}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.opening)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.debit)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.credit)}</td>
                          <td
                            className={cn(
                              AMT_CLS, 'font-medium',
                              r.closing < 0
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-900 dark:text-gray-100',
                            )}
                          >
                            {fmtAmt(r.closing)}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {r.transactionCount.toLocaleString('en-US')}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.averageValue)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.largestTransaction)}</td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>{fmtAmt(r.smallestTransaction)}</td>
                          <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {fmtDate(r.lastUsedDate)}
                          </td>
                          <td className={cn(AMT_CLS, 'text-gray-700 dark:text-gray-300')}>
                            {r.daysSinceLastUsed === null ? '—' : r.daysSinceLastUsed.toLocaleString('en-US')}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Delta percent={r.growthPercent} />
                          </td>
                          <td className="px-3 py-2">
                            {r.flags.length === 0 ? (
                              <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {r.flags.map(f => <FlagBadge key={f} flag={f} />)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Account Activity Analysis"
          orientation="landscape"
        >
          <ReportHeader
            reportTitle="Account Activity Analysis"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company',  value: data.companyName },
              { label: 'Period',   value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              { label: 'Accounts', value: String(data.totalAccounts) },
              { label: 'Inactive', value: String(data.inactiveAccounts) },
              { label: 'Flagged',  value: String(data.flaggedAccounts) },
            ]}
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5px', marginTop: '8px' }}>
            <thead>
              <tr>
                {['Code', 'Account Name', 'Type', 'Opening', 'Debit', 'Credit', 'Closing', 'Txns', 'Last Used', 'Flags'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      background: '#1e3a5f', color: 'white', padding: '5px 6px',
                      textAlign: i >= 3 && i <= 7 ? 'right' : 'left',
                      fontWeight: 700, whiteSpace: 'nowrap', fontSize: '8.5px',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.accountId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '3px 6px', fontFamily: 'monospace', color: '#6b7280' }}>{r.accountCode}</td>
                  <td style={{ padding: '3px 6px' }}>{r.accountName}</td>
                  <td style={{ padding: '3px 6px', color: '#6b7280', fontSize: '8px' }}>{r.accountType}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(r.opening)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(r.debit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(r.credit)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtAmt(r.closing)}</td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{r.transactionCount}</td>
                  <td style={{ padding: '3px 6px' }}>{fmtDate(r.lastUsedDate)}</td>
                  <td style={{ padding: '3px 6px', fontSize: '8px' }}>
                    {r.flags.map(f => FLAG_LABELS[f]).join(', ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
